/**
 * @file Experiences.page.tsx
 * @path apps/website/src/pages/Experiences.page.tsx
 * @description Page component fully integrated using the Facade pattern.
 */

import { shuttle2 } from "@/assets";
import { scene3 } from "@/assets/videos";
import { CallToAction, HeroSection, SeoHelmet } from "@/components";
import { PATHS } from "@/config";

// Feature Imports
import { useExperiencesFacade } from "@/features/experiences/hooks/useExperiencesFacade.hook";
import { ExperienceFilters } from "@/features/experiences/components/experienceFilters.component";
import { ExperienceDrawer } from "@/features/experiences/components/experienceDrawer.component";
import { ExperienceList } from "@/features/experiences/components/experienceList.component";
import { FilterChip } from "@/features/experiences/components/filterChip.component";

export const ExperiencesPage = () => {
  const facade = useExperiencesFacade();
  const { data, state, actions } = facade;

  return (
    <>
      <SeoHelmet
        pageType="serviceList"
        title="Experiences | Sir Bastian's Shuttle Service"
      />

      <HeroSection
        title="Experience Luxury"
        subtitle="You Deserve"
        paragraph="Discover exciting adventures, cultural experiences, and natural wonders that make Cape Town unforgettable"
        actions={[{ text: "Start Exploring", scrollTo: "experiences-content" }]}
        videoSrc={scene3}
        playbackRate={0.8}
      />

      <div id="experiences-content" className="min-h-screen bg-background text-foreground font-open-sans flex flex-col lg:flex-row">
        {/* Mobile Drawer wrapper */}
        <ExperienceDrawer facade={facade} />

        {/* Desktop Sidebar Filters */}
        <aside className="hidden lg:flex flex-col w-80 border-r border-border p-8 sticky top-0 h-screen overflow-y-auto bg-card">
          <ExperienceFilters
            categories={data.categories}
            activeCategory={state.activeCategory}
            setActiveCategory={actions.setCategory}
            searchQuery={state.searchQuery}
            setSearchQuery={actions.setSearchQuery}
            itemCounts={data.itemCounts}
            maxPrice={state.maxPrice}
            absoluteMaxPrice={data.absoluteMaxPrice}
            priceHistogram={data.priceHistogram} // ✨ NEW
            setMaxPrice={actions.setMaxPrice}
          />
        </aside>

        {/* Main Content Grid */}
        <main className="flex-1 p-6 md:p-16">
          <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-montserrat font-bold mb-4">
                {state.activeCategory === "All"
                  ? "Every Experience"
                  : state.activeCategory}
              </h1>
              <div className="flex flex-wrap gap-2">
                {state.activeCategory !== "All" && (
                  <FilterChip
                    label={state.activeCategory}
                    active
                    onRemove={() => actions.setCategory("All")}
                  />
                )}
                {state.searchQuery && (
                  <FilterChip
                    label={`Search: ${state.searchQuery}`}
                    onRemove={() => actions.setSearchQuery("")}
                  />
                )}
              </div>
            </div>
          </header>

          <ExperienceList
            items={data.filteredItems}
            onReset={actions.resetFilters}
          />
        </main>
      </div>

      <CallToAction
        title="Your Journey, Our Commitment"
        description="Punctuality, safety, and comfort are at the core of our experience."
        primaryAction={{ text: "Book Your Ride", href: PATHS.BOOKING }}
        secondaryAction={{ text: "Our Activities", href: PATHS.EXPERIENCES }}
        backgroundImage={{ src: shuttle2, alt: "Sunrise over Cape Town" }}
      />
    </>
  );
};
